/**
 * Editor document -> Markdown (TASK-402).
 *
 * The editor model and the published model are different representations
 * (AGENTS.md §11): TipTap's ProseMirror JSON is what the editor works with,
 * Markdown + frontmatter is what gets committed. This is the one-way bridge
 * between them.
 *
 * It is deliberately dependency-free — a walk over the document tree rather
 * than another parser — so there is exactly one place where the mapping is
 * defined and one place to change it.
 */

/** A ProseMirror text mark. */
export interface ProseMirrorMark {
  readonly type: string;
  readonly attrs?: Record<string, unknown> | undefined;
}

/** A ProseMirror node. */
export interface ProseMirrorNode {
  readonly type: string;
  readonly attrs?: Record<string, unknown> | undefined;
  readonly content?: readonly ProseMirrorNode[] | undefined;
  readonly text?: string | undefined;
  readonly marks?: readonly ProseMirrorMark[] | undefined;
}

/** A ProseMirror document. */
export interface ProseMirrorDocument extends ProseMirrorNode {
  readonly type: "doc";
}

/**
 * Characters that would otherwise start a Markdown construct mid-sentence.
 *
 * Escaping is applied only to text outside code spans and code blocks, where a
 * backslash would be shown to the reader instead of being interpreted.
 */
const ESCAPABLE = /[\\`*_[\]<>#]/g;

/** Escape inline text so it round-trips as literal characters. */
export function escapeInlineText(text: string): string {
  return text.replace(ESCAPABLE, (char) => `\\${char}`);
}

/**
 * Escape a table cell.
 *
 * A pipe inside a cell would end the column, and a newline would end the row.
 */
function escapeTableCell(text: string): string {
  return escapeInlineText(text).replace(/\|/g, "\\|").replace(/\r?\n/g, "<br>");
}

/**
 * Sanitize a link or image URL for Markdown output.
 *
 * The renderer sanitizes on the way out, but a `javascript:` URL written into a
 * committed Markdown file is a landmine for anything else that reads the
 * repository. Dropping it here means it never reaches the file.
 */
export function safeUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.trim() === "") {
    return null;
  }

  const trimmed = value.trim();

  // Allow relative URLs and fragment links; they cannot carry a scheme.
  if (/^[/#]/.test(trimmed)) {
    return trimmed;
  }

  // eslint-disable-next-line no-control-regex -- control characters are exactly what we are screening for
  if (/[\u0000-\u001f]/.test(trimmed)) {
    return null;
  }

  if (/^(https?:|mailto:)/i.test(trimmed)) {
    return trimmed;
  }

  return null;
}

/** Wrap `text` in `marker` on both sides. */
function applyMark(text: string, marker: string): string {
  return text === "" ? text : `${marker}${text}${marker}`;
}

/**
 * Render the inline content of a node, applying marks.
 *
 * Marks are applied in a fixed order (innermost first) so that overlapping
 * emphasis produces deterministic output rather than depending on the order
 * TipTap happened to store them in.
 */
const MARK_ORDER = ["code", "bold", "italic", "underline", "strike", "link"] as const;

interface InlineOptions {
  /** Inside a heading, where underline is not representable (see below). */
  readonly inHeading?: boolean;
}

function serializeInline(
  nodes: readonly ProseMirrorNode[] | undefined,
  options: InlineOptions = {},
): string {
  if (nodes === undefined) {
    return "";
  }

  let out = "";

  for (const node of nodes) {
    if (node.type === "hardBreak") {
      out += "  \n";
      continue;
    }

    if (node.type === "image") {
      out += serializeImage(node);
      continue;
    }

    if (node.type !== "text") {
      // Unknown inline node: keep its text rather than dropping content.
      out += serializeInline(node.content, options);
      continue;
    }

    let text = node.text ?? "";

    // Code spans are literal: escaping inside them would show the backslashes.
    const inCode = node.marks?.some((mark) => mark.type === "code") === true;
    text = inCode ? text : escapeInlineText(text);

    const marks = node.marks ?? [];

    for (const type of [...MARK_ORDER].reverse()) {
      const mark = marks.find((candidate) => candidate.type === type);

      if (mark === undefined) {
        continue;
      }

      // Underline inside a heading is Word-import styling, not intent
      // (normalize.ts strips it on the way in); the serializer agrees, so a
      // heading never carries `<u>` in either direction of the round trip.
      if (type === "underline" && options.inHeading === true) {
        continue;
      }

      switch (type) {
        case "code":
          text = applyMark(text, "`");
          break;
        case "bold":
          text = applyMark(text, "**");
          break;
        case "italic":
          text = applyMark(text, "*");
          break;
        case "underline":
          // Markdown has no underline marker; inline HTML is the portable
          // spelling, and the site's sanitizer allows `<u>` (and only `<u>`).
          text = `<u>${text}</u>`;
          break;
        case "strike":
          text = applyMark(text, "~~");
          break;
        case "link": {
          const href = safeUrl(mark.attrs?.href);

          if (href !== null) {
            text = `[${text}](${href})`;
          }
          break;
        }
      }
    }

    out += text;
  }

  return out;
}

function serializeImage(node: ProseMirrorNode): string {
  const src = safeUrl(node.attrs?.src);
  const alt = typeof node.attrs?.alt === "string" ? node.attrs.alt : "";
  const title = typeof node.attrs?.title === "string" ? node.attrs.title : undefined;

  // An image with an unusable src would render as a broken image; the alt text
  // is more useful to a reader than an empty box.
  if (src === null) {
    return alt === "" ? "" : escapeInlineText(alt);
  }

  const titlePart = title === undefined || title === "" ? "" : ` "${title.replace(/"/g, '\\"')}"`;

  return `![${alt}](${src}${titlePart})`;
}

/**
 * Prefix every line, including blank ones.
 *
 * A blank line inside a blockquote still has to carry the marker: without it
 * the quote ends and a second one begins, which parses as a different document.
 */
function indent(text: string, prefix: string): string {
  const blankPrefix = prefix.trimEnd();

  return text
    .split("\n")
    .map((line) => (line === "" ? blankPrefix : `${prefix}${line}`))
    .join("\n");
}

/**
 * Serialize a ProseMirror document to Markdown.
 *
 * Block-level nodes are joined by a blank line, which is what makes the output
 * stable under a Markdown round-trip.
 */
export function serializeDocument(doc: ProseMirrorDocument): string {
  const blocks = (doc.content ?? [])
    .map((node) => serializeBlock(node))
    .filter((block) => block !== "");

  return blocks.length === 0 ? "" : `${blocks.join("\n\n")}\n`;
}

function serializeBlock(node: ProseMirrorNode): string {
  switch (node.type) {
    case "paragraph": {
      const text = serializeInline(node.content).trimEnd();
      // An empty paragraph is meaningful in the editor but not in Markdown.
      return text;
    }

    case "heading": {
      const level = clampHeadingLevel(node.attrs?.level);
      return `${"#".repeat(level)} ${serializeInline(node.content, { inHeading: true }).trim()}`;
    }

    case "codeBlock": {
      const language = typeof node.attrs?.language === "string" ? node.attrs.language : "";
      const code = (node.content ?? []).map((child) => child.text ?? "").join("");
      return `\`\`\`${language}\n${code.replace(/\n$/, "")}\n\`\`\``;
    }

    case "blockquote": {
      const inner = (node.content ?? [])
        .map((child) => serializeBlock(child))
        .filter((block) => block !== "")
        .join("\n\n");

      return indent(inner, "> ");
    }

    case "bulletList":
      return serializeList(node, () => "-");

    case "orderedList":
      return serializeList(node, (index) => `${String(index + 1)}.`);

    case "taskList":
      return serializeList(node, () => "-", true);

    case "horizontalRule":
      return "---";

    case "table":
      return serializeTable(node);

    case "image":
      return serializeImage(node);

    default: {
      // An unknown block: fall back to its inline content so nothing is lost,
      // rather than emitting a placeholder the author cannot see in the editor.
      const inline = serializeInline(node.content).trim();

      if (inline !== "") {
        return inline;
      }

      const children = (node.content ?? [])
        .map((child) => serializeBlock(child))
        .filter((block) => block !== "");

      return children.join("\n\n");
    }
  }
}

function clampHeadingLevel(value: unknown): number {
  const level = typeof value === "number" ? value : 1;

  return Math.min(6, Math.max(1, Math.round(level)));
}

function serializeList(
  node: ProseMirrorNode,
  marker: (index: number) => string,
  isTask = false,
): string {
  const items = node.content ?? [];

  return items
    .map((item, index) => {
      const checkbox = isTask ? `${item.attrs?.checked === true ? "[x]" : "[ ]"} ` : "";
      const firstPrefix = `${marker(index)} ${checkbox}`;
      // Continuation lines align under the text, which is what makes a
      // multi-paragraph list item parse back as a single item.
      const continuationIndent = " ".repeat(firstPrefix.length);

      const inner = (item.content ?? [])
        .map((child) => serializeBlock(child))
        .filter((block) => block !== "")
        .join("\n\n");

      const [first, ...rest] = inner.split("\n");
      const head = `${firstPrefix}${first ?? ""}`;
      const tail = rest
        .map((line) => (line === "" ? "" : `${continuationIndent}${line}`))
        .join("\n");

      return tail === "" ? head : `${head}\n${tail}`;
    })
    .join("\n");
}

function serializeTable(node: ProseMirrorNode): string {
  const rows = node.content ?? [];
  const rendered: string[][] = [];
  let headerCount = 0;

  for (const row of rows) {
    const cells: string[] = [];

    for (const cell of row.content ?? []) {
      cells.push(escapeTableCell(serializeInline(cell.content).trim()));
    }

    if (row.type === "tableHeader") {
      headerCount = cells.length;
    }

    rendered.push(cells);
  }

  if (rendered.length === 0) {
    return "";
  }

  const columnCount = Math.max(
    headerCount,
    ...rendered.map((cells) => cells.length),
  );

  const pad = (cells: string[]): string[] =>
    Array.from({ length: columnCount }, (_, index) => cells[index] ?? "");

  const header = pad(rendered[0] ?? []);
  const body = rendered.slice(1).map(pad);

  // GFM requires a header row; a table that starts with body rows gets an empty
  // one so the delimiter row has something to attach to.
  const lines = [
    `| ${header.join(" | ")} |`,
    `| ${Array.from({ length: columnCount }, () => "---").join(" | ")} |`,
    ...body.map((cells) => `| ${cells.join(" | ")} |`),
  ];

  return lines.join("\n");
}
